import React from 'react';
import { setUserSettings, SaveApiResMessage } from '@/actions';
import { noScroll, removeNoScroll } from '@/actions/Helper';
import { Input } from '@/components/Input';
import IconButton from '@/ctComponents/IconButton';
import Modal from '@/ctComponents/Modal';
import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import * as yup from 'yup';
import { SAVING_FIELDS } from './estimated-saving';

const formSchema = yup.object().shape({
  savingChassisFee: yup
    .number()
    .required('Chassis Fee is required')
    .min(0, 'Chassis Fee must be 0 or greater')
    .typeError('Chassis Fee must be a valid number'),
  savingContainerMovingRate: yup
    .number()
    .required('Container Moving Rate is required')
    .min(0, 'Container Moving Rate must be 0 or greater')
    .typeError('Container Moving Rate must be a valid number'),
});

export const EditEstimatedSaving = ({ data, show, setShow, onSave }) => {
  // const notify = useNotify();

  const [formData, setFormData] = useState({
    savingLmcFee: '',
    savingContainerMovingRate: '',
    savingPrePullRate: '',
    savingChassisFee: '',
    savingChassisSplitFee: '',
    savingStorageFee: '',
    savingFuelSurgeCharge: '',
    savingCongestionFee: '',
    savingDetentionFee: '',
    savingScaleFee: '',
    savingFlatFee: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({
        savingLmcFee: data.savingLmcFee || '',
        savingContainerMovingRate: data.savingContainerMovingRate || '',
        savingPrePullRate: data.savingPrePullRate || '',
        savingChassisFee: data.savingChassisFee || '',
        savingChassisSplitFee: data.savingChassisSplitFee || '',
        savingStorageFee: data.savingStorageFee || '',
        savingFuelSurgeCharge: data.savingFuelSurgeCharge || '',
        savingCongestionFee: data.savingCongestionFee || '',
        savingDetentionFee: data.savingDetentionFee || '',
        savingScaleFee: data.savingScaleFee || '',
        savingFlatFee: data.savingFlatFee || '',
      });
    }
  }, [data]);

  useEffect(() => {
    show ? noScroll() : removeNoScroll();
  }, [show]);

  const validateForm = async () => {
    try {
      await formSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error) {
      const validationErrors = {};
      error.inner.forEach((err) => {
        validationErrors[err.path] = err.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = async () => {
    setIsSubmitting(true);

    try {
      const isValid = await validateForm();

      if (!isValid) {
        // notify('Please fix the errors before submitting', 'error');
        SaveApiResMessage({
          title: 'Error',
          message: 'Please fix the errors before submitting',
          mode: 'error',
        });
        setIsSubmitting(false);
        return;
      }

      const req = {
        key: 'streetTurnPreferencesData',
        data: formData,
      };

      await setUserSettings(req);
      // notify('Estimated saving settings updated successfully', 'success');
      SaveApiResMessage({
        title: data?.messageHeader,
        message: 'Estimated saving settings updated successfully',
        mode: 'success',
      });
      setShow(false);

      if (onSave) {
        onSave();
      }
    } catch (error) {
      // notify(error, 'error');
      SaveApiResMessage({
        title: 'Error',
        message: error,
        mode: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (data) {
      setFormData({
        savingLmcFee: data.savingLmcFee || '',
        savingContainerMovingRate: data.savingContainerMovingRate || '',
        savingPrePullRate: data.savingPrePullRate || '',
        savingChassisFee: data.savingChassisFee || '',
        savingChassisSplitFee: data.savingChassisSplitFee || '',
        savingStorageFee: data.savingStorageFee || '',
        savingFuelSurgeCharge: data.savingFuelSurgeCharge || '',
        savingCongestionFee: data.savingCongestionFee || '',
        savingDetentionFee: data.savingDetentionFee || '',
        savingScaleFee: data.savingScaleFee || '',
        savingFlatFee: data.savingFlatFee || '',
      });
    }
    setErrors({});
    setShow(false);
  };

  return (
    <Modal
      minWidth="unset"
      show={show}
      noX
      notClose={true}
      children={
        <div className="py-4 w-full overflow-y-auto space-y-4 max-w-sm sm:max-w-none sm:min-w-640">
          <div className="px-4">
            <div className=" gap-4 md:grid grid-cols-2">
              {SAVING_FIELDS.map((field) => (
                <Input
                  key={field.label}
                  label={field.shortLabel || field.label}
                  adornment={field.shortUnit || field.unit}
                  name={field.name}
                  id={field.name}
                  placeholder="0.00"
                  error={errors[field.name]}
                  value={formData[field.name]}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  type="number"
                  step="0.1"
                  required={field.required}
                  min="0"
                />
              ))}
            </div>
            <div className="flex justify-end gap-4 mt-6 pt-4 ">
              <IconButton
                onClick={handleCancel}
                text="Cancel"
                color="#131617"
                size="normal"
                style={{
                  minWidth: 120,
                  height: 40,
                }}
              />
              <IconButton
                onClick={handleSave}
                text={isSubmitting ? 'Saving...' : 'Save Changes'}
                color="#FFAD00"
                size="normal"
                disabled={isSubmitting}
                style={{
                  backgroundColor: '#FFAD00',
                  color: '#131617',
                  opacity: isSubmitting ? 0.7 : 1,
                  minWidth: 120,
                  height: 40,
                }}
              />
            </div>
          </div>
        </div>
      }
      title="Edit Estimated Saving"
      loadingbutton={isSubmitting}
      icon={'pen'}
      setShow={setShow}
    />
  );
};
